<template>
    <div
        class="ppv2-w-full ppv2-bg-white dark:ppv2-bg-dark ppv2-rounded-xl ppv2-border ppv2-border-gray-200 ppv2-p-4 ppv2-flex ppv2-flex-col"
        style="gap: 10px"
    >
        <div class="ppv2-flex ppv2-items-start ppv2-justify-between" style="gap: 16px">
            <div>
                <div class="poppinsSemiBold ppv2-text-black" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ $t('forms.faq.title') }} · {{ faqs.length }}
                </div>
                <div class="poppinsRegular" :style="{ fontSize: normalizeFontSize(12.5), color: '#6b7480' }">
                    {{ $t('forms.faq.subtitle') }}
                </div>
            </div>
            <button
                type="button"
                class="ppv2-shrink-0 ppv2-inline-flex ppv2-items-center ppv2-gap-[7px] ppv2-h-[34px] ppv2-px-3 ppv2-rounded-lg ppv2-border ppv2-border-solid ppv2-border-gray-200 ppv2-bg-white dark:ppv2-bg-dark ppv2-text-black poppinsMedium hover:ppv2-bg-[#7d2dbd] hover:ppv2-bg-opacity-10 hover:ppv2-border-gray-300 ppv2-transition-colors ppv2-cursor-pointer"
                :style="{ fontSize: normalizeFontSize(13) }"
                @click="$emit('propose')"
            >
                {{ $t('forms.faq.propose') }}
            </button>
        </div>

        <div
            v-for="(faq, i) in faqs"
            :key="i"
            class="ppv2-rounded-[10px] ppv2-bg-white dark:ppv2-bg-dark ppv2-overflow-hidden"
            :class="isOpen(i) ? 'ppv2-border ppv2-border-[#dbc1f0]' : 'ppv2-border ppv2-border-gray-200'"
        >
            <div
                class="ppv2-flex ppv2-items-center ppv2-select-none hover:ppv2-bg-[#7d2dbd] hover:ppv2-bg-opacity-10 ppv2-transition-colors"
                style="gap: 11px; padding: 11px 12px; cursor: pointer"
                @click="toggle(i)"
            >
                <span
                    class="bg-purple ppv2-bg-opacity-10 ppv2-shrink-0 ppv2-w-6 ppv2-h-6 ppv2-rounded-[7px] ppv2-flex ppv2-items-center ppv2-justify-center poppinsSemiBold"
                    style="color: #7d2dbd; font-size: 11.5px"
                >
                    {{ i + 1 }}
                </span>
                <span
                    class="poppinsMedium ppv2-flex-1 ppv2-min-w-0 ppv2-truncate"
                    :class="faq.question ? 'ppv2-text-black' : 'ppv2-text-black ppv2-text-opacity-70'"
                    :style="{ fontSize: normalizeFontSize(13.5) }"
                >
                    {{ faq.question || $t('forms.faq.questionPlaceholder') }}
                </span>
                <button
                    type="button"
                    class="ppv2-shrink-0 ppv2-w-[26px] ppv2-h-[26px] ppv2-rounded-md ppv2-flex ppv2-items-center ppv2-justify-center ppv2-border-0 ppv2-bg-transparent ppv2-text-black ppv2-text-opacity-60 hover:ppv2-bg-[#7d2dbd] hover:ppv2-bg-opacity-10 ppv2-transition-colors ppv2-cursor-pointer disabled:ppv2-cursor-not-allowed disabled:ppv2-opacity-30 disabled:hover:ppv2-bg-transparent"
                    :disabled="i === 0"
                    :title="$t('forms.question.moveUp')"
                    @click.stop="$emit('move', i, -1)"
                >
                    <FormTypeIcon name="arrow-up" :size="13" />
                </button>
                <button
                    type="button"
                    class="ppv2-shrink-0 ppv2-w-[26px] ppv2-h-[26px] ppv2-rounded-md ppv2-flex ppv2-items-center ppv2-justify-center ppv2-border-0 ppv2-bg-transparent ppv2-text-black ppv2-text-opacity-60 hover:ppv2-bg-black hover:ppv2-bg-opacity-5 ppv2-transition-colors ppv2-cursor-pointer disabled:ppv2-cursor-not-allowed disabled:ppv2-opacity-30 disabled:hover:ppv2-bg-transparent"
                    :disabled="i === faqs.length - 1"
                    :title="$t('forms.question.moveDown')"
                    @click.stop="$emit('move', i, 1)"
                >
                    <FormTypeIcon name="arrow-down" :size="13" />
                </button>
                <button
                    type="button"
                    class="ppv2-shrink-0 ppv2-w-[26px] ppv2-h-[26px] ppv2-rounded-md ppv2-flex ppv2-items-center ppv2-justify-center ppv2-border-0 ppv2-bg-transparent ppv2-text-black ppv2-text-opacity-60 hover:ppv2-bg-[#fdeceb] hover:ppv2-text-[#c0392b] ppv2-transition-colors ppv2-cursor-pointer"
                    :title="$t('forms.faq.delete')"
                    @click.stop="$emit('remove', i)"
                >
                    <FormTypeIcon name="close" :size="14" />
                </button>
                <span
                    class="ppv2-shrink-0 ppv2-w-5 ppv2-inline-flex ppv2-items-center ppv2-justify-center"
                    style="color: #cbd2dc; font-size: 11px"
                >
                    <FormTypeIcon
                        name="chevron-down"
                        :size="14"
                        class="ppv2-transition-transform"
                        :class="isOpen(i) ? 'ppv2-rotate-180' : ''"
                    />
                </span>
            </div>
            <div
                v-if="isOpen(i)"
                class="ppv2-flex ppv2-flex-col ppv2-bg-white dark:ppv2-bg-dark"
                style="border-top: 1px solid #eef1f5; padding: 14px; gap: 12px"
            >
                <CustomInput
                    v-model="faq.question"
                    :label="$t('forms.faq.question')"
                    :placeholder="$t('forms.faq.questionPlaceholder')"
                    class-container-add="!ppv2-mb-0"
                    customSizeLabel="12.5"
                />
                <BaseTextarea
                    v-model="faq.response"
                    :label="$t('forms.faq.answer')"
                    :placeholder="$t('forms.faq.answerPlaceholder')"
                    :rows="3"
                    :use-editor="false"
                    class-container-add="!ppv2-mb-0"
                    customSizeLabel="12.5"
                />
            </div>
        </div>

        <button
            type="button"
            class="ppv2-w-full ppv2-h-[48px] ppv2-mt-1 ppv2-rounded-xl ppv2-border-[1.5px] ppv2-border-dashed ppv2-border-gray-300 ppv2-text-gray-500 hover:ppv2-border-[#7D2DBD] hover:ppv2-text-[#7D2DBD] hover:ppv2-bg-[#7d2dbd] hover:ppv2-bg-opacity-10 ppv2-transition-colors ppv2-flex ppv2-items-center ppv2-justify-start ppv2-px-3.5 ppv2-gap-2 poppinsRegular"
            :style="{ fontSize: normalizeFontSize(13.5) }"
            @click="$emit('add')"
        >
            <FormTypeIcon name="plus" :size="15" />
            {{ $t('forms.faq.add') }}
        </button>
    </div>
</template>

<script>
import normalizeFontSize from '../../../../utils/normalizeFontSize'
import FormTypeIcon from './FormTypeIcon.vue'
import CustomInput from '../../../Inputs/CustomInput.vue'
import BaseTextarea from '../../../Shared/BaseTextarea.vue'

export default {
    name: 'FormFAQEditor',
    components: { FormTypeIcon, CustomInput, BaseTextarea },
    props: {
        faqs: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            openIndex: null,
        }
    },
    watch: {
        'faqs.length'(length, oldLength) {
            if (length > oldLength) {
                this.openIndex = length - 1
            }
        },
    },
    methods: {
        normalizeFontSize,
        isOpen(i) {
            return this.openIndex === i
        },
        toggle(i) {
            this.openIndex = this.openIndex === i ? null : i
        },
    },
}
</script>
